import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  ButtonGroup,
  Button,
  Stack,
  Avatar,
} from "@mui/material";
import {
  MoreVert,
  Check,
  Person,
  Image,
  Warning,
  Close,
  Block,
  Delete,
} from "@mui/icons-material";
import ImageLoader from "../ImageLoader";

const getStatusColor = (status) => {
  switch (status) {
    case "OPEN":
      return "warning";
    case "CLOSED":
      return "success";
    case "APPEAL":
      return "error";
    default:
      return "default";
  }
};

const getActionIcon = (action) => {
  switch (action) {
    case "BLOCK":
      return <Block fontSize="small" />;
    case "DELETE":
      return <Delete fontSize="small" />;
    case "WARN":
      return <Warning fontSize="small" />;
    default:
      return null;
  }
};

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getReportTypeColor = (reportType) => {
  const dangerTypes = ["HATEFULL", "SEXUAL", "VIOLENCE", "THREATS", "SEXUAL_HARASSMENT", "RACIST"];
  const warningTypes = ["SELF-HARM", "VULGAR", "SPAM"];
  const infoTypes = ["FAKE", "COPYRIGHT", "IMPERSONATE", "OTHER"];
  
  if (dangerTypes.includes(reportType)) return "error";
  if (warningTypes.includes(reportType)) return "warning";
  if (infoTypes.includes(reportType)) return "info";
  return "default";
};

const getAIValidationColor = (validation) => {
  if (validation === "SAFE") return "success";
  if (["HATEFULL", "VIOLENCE", "SEXUAL"].includes(validation)) return "error";
  if (validation === "SELF-HARM") return "warning";
  return "default";
};

export default function ReportCard({ report, onDeny, onAction }) {
  // Determine which report type to display based on report.type
  const getReportTypeField = () => {
    if (report.type === "PROFILE" && report.profileReportType) {
      return { label: "Profile Issue ", value: report.profileReportType };
    }
    if (report.type === "MESSAGE" && report.messageReportType) {
      return { label: "Message Issue ", value: report.messageReportType };
    }
    if (report.contentReportType) {
      return { label: "Content Issue ", value: report.contentReportType };
    }
    return null;
  };

  const reportTypeField = getReportTypeField();
  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
        },
      }}
    >
      {/* Header */}
      <CardHeader
        avatar={
          <ImageLoader
            source={{ uri: report.reportedUser?.profile?.profilePicture?.url }}
            alt={report.reportedUser?.fullname || "User"}
            style={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              objectFit: "cover",
              objectPosition: "top", // <-- centers face/head
              border: "2px solid rgba(199, 164, 93, 0.6)",
              mt: 20,
            }}
          />
        }
        action={
          <IconButton>
            <MoreVert />
          </IconButton>
        }
        title={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={600} noWrap p={0} m={0}>
              {report.reportedUser?.fullname || "Unknown User"}
            </Typography>
            {report.reportedUser?.profile?.isVerified && (
              <Tooltip title="Verified profile">
                <Check color="primary" fontSize="small" />
              </Tooltip>
            )}
          </Stack>
        }
        subheader={
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mt={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
              <Chip
                label={report.type}
                size="small"
                icon={report.type === "PROFILE" ? <Person /> : <Image />}
              />
              <Chip
                label={report.status}
                size="small"
                color={getStatusColor(report.status)}
                sx={{ fontWeight: 500 }}
              />
              {report.action && (
                <Tooltip title={`Action: ${report.action}`}>
                  <Chip
                    size="small"
                    icon={getActionIcon(report.action)}
                    sx={{
                      pl: 1,
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            {(reportTypeField || (report.AIValidation && report.AIValidation !== "SAFE")) && (
              <Stack direction="row" alignItems="center" spacing={1} mt={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                {reportTypeField && (
                  <Chip
                    label={reportTypeField.value.replace(/_/g, ' ')}
                    size="small"
                    color={getReportTypeColor(reportTypeField.value)}
                    variant="outlined"
                  />
                )}
                {report.AIValidation && report.AIValidation !== "SAFE" && (
                  <Tooltip title="AI Validation">
                    <Chip
                      label={`AI: ${report.AIValidation}`}
                      size="small"
                      color={getAIValidationColor(report.AIValidation)}
                      sx={{ fontWeight: 600 }}
                    />
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>
        }
      />

      {/* Content */}
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              py={1}
              px={0}
              m={0}
            >
              Reported by
            </Typography>
            <Typography variant="body1" fontWeight={500} py={1} px={0} m={0}>
              {report.reportedBy?.fullname || "Unknown"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              py={1}
              px={0}
              m={0}
            >
              Reported on
            </Typography>
            <Typography variant="body1" py={1} px={0} m={0}>
              {formatDate(report.createdAt)}
            </Typography>
          </Box>

          <Divider />

          {reportTypeField && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                py={1}
                px={0}
                m={0}
              >
                {reportTypeField.label}
              </Typography>
              <Chip
                label={reportTypeField.value.replace(/_/g, ' ')}
                size="small"
                color={getReportTypeColor(reportTypeField.value)}
              />
            </Box>
          )}

          {report.AIValidation && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                py={1}
                px={0}
                m={0}
              >
                AI Validation
              </Typography>
              <Chip
                label={report.AIValidation}
                size="small"
                color={getAIValidationColor(report.AIValidation)}
                icon={report.AIValidation === "SAFE" ? <Check fontSize="small" /> : <Warning fontSize="small" />}
              />
            </Box>
          )}

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              py={1}
              px={0}
              m={0}
            >
              Details
            </Typography>
            <Typography variant="body1" py={1} px={0} m={0}>
              {report.details || "No details provided"}
            </Typography>
          </Box>

          {report.messageText && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                py={1}
                px={0}
                m={0}
              >
                Message Text
              </Typography>
              <Typography variant="body1" py={1} px={0} m={0}>
                {report.messageText}
              </Typography>
            </Box>
          )}

          {report.response && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                py={1}
                px={0}
                m={0}
              >
                Response
              </Typography>
              <Typography variant="body2" fontWeight={500} py={1} px={0} m={0}>
                {report.response}
              </Typography>
            </Box>
          )}

          {report.responseMsg && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                py={1}
                px={0}
                m={0}
              >
                Message
              </Typography>
              <Typography variant="body2" py={1} px={0} m={0}>
                {report.responseMsg}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
        <ButtonGroup variant="outlined" size="medium" fullWidth>
          <Button
            color="error"
            startIcon={<Close />}
            onClick={() => onDeny(report._id)}
            disabled={report.status === "CLOSED"}
          >
            Dismiss
          </Button>
          <Button
            color="primary"
            startIcon={<Warning />}
            onClick={() => onAction(report)}
            disabled={report.status === "CLOSED"}
          >
            Action
          </Button>
        </ButtonGroup>
      </CardActions>
    </Card>
  );
}
